import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  Spin,
  Form,
  Button,
  Tooltip,
  Typography,
  Table,
  Breadcrumb,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import get from "lodash/get";
import dayjs from "dayjs";
import requireAuth from "../../hocs/requireAuth";
import {
  getPerformanceReportColumn,
  generateDataSource,
} from "../../constants/tableColumns/summaryReportsColumns/getPerformanceReportColumn";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { RadarChartOutlined, StockOutlined } from "@ant-design/icons";
import { fetchPerformanceReport } from "../../redux/actions/reportActions";
import { fetchPerformanceReportPdf } from "../../redux/actions/downloadPdfAction";
import { fetchCompanyTemplates } from "../../redux/actions/templateActions";
import HeaderSelector from "../../components/reports/performanceReport/headerSelector";
import { useExcelExport } from "../../hooks/useExcelExport";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <RadarChartOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <StockOutlined style={{ fontSize: "16px" }} />
              <span>Performance Report</span>
            </>
          ),
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  template: undefined,
  date: dayjs(),
};

const PerformanceReportScreen = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [performanceReport, setPerformanceReport] = useState(undefined);
  const [filter, setFilters] = useState(initialState);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { wrapperRef, onDownload, isReady } = useExcelExport(
    [performanceReport, getPerformanceReportColumn],
    `Performance-Report-${get(filter, "date", "").format("YYYY-MM-DD")}`,
    `Performance-Report`
  );
  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
      template_types: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template_type: undefined,
      template: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getCompanyTemplates = (type) => {
    setLoading(true);
    dispatch(fetchCompanyTemplates(get(filter, "company.id"), { type }))
      .then((result) => {
        setConfig({
          ...config,
          templates: result,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getDetails = () => {
    setLoading(true);
    const query = {
      date: get(filter, "date").format("YYYY-MM-DD"),
    };
    dispatch(fetchPerformanceReport(get(filter, "template.id"), query))
      .then((result) => {
        setPerformanceReport(result);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      date: get(filter, "date").format("YYYY-MM-DD"),
    };
    dispatch(
      fetchPerformanceReportPdf(get(filter, "template.id"), {
        template_name: get(filter, "template.name"),
        ...query,
      })
    )
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);
  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getCompanyTemplates={getCompanyTemplates}
              getDetails={getDetails}
              setPerformanceReport={setPerformanceReport}
            />
            {performanceReport && (
              <>
                <div className="flex justify-end gap-4 items-center w-full px-8">
                  <Tooltip className="float-right mt-2" title="Download Report">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadReport()}
                    />
                  </Tooltip>
                  <Tooltip
                    title="Download Performace Excel Report"
                    className="float-right mt-2"
                  >
                    <ExcelExportButton
                      onDownload={onDownload}
                      disabled={!isReady}
                    />
                  </Tooltip>
                </div>
                <div className="w-full px-8 overflow-y-auto">
                  <div className="w-full table-container" ref={wrapperRef}>
                    <Table
                      size="small"
                      scroll={{ x: 'max-content' }}
                      title={() => (
                        <div className="flex flex-row justify-between pr-10">
                          <Title
                            className="mx-4 my-4"
                            level={4}
                          >{`Performance Details : ${get(
                            filter,
                            "template.name"
                          )}`}</Title>
                        </div>
                      )}
                      className="mb-20 mt-10 custom-horizontal-scrollbar"
                      dataSource={generateDataSource(
                        get(performanceReport, "report"),
                        get(filter, "templateType")
                      )}
                      columns={getPerformanceReportColumn(
                        get(performanceReport, "report")
                      )}
                      bordered
                      pagination={false}
                     rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                    />
                  </div>
                </div>
              </>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(PerformanceReportScreen, COMPANY_ADMIN_ROLE_ACCESS);
